import React, {useEffect, useState} from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { useNavigate } from "react-router-dom";
import styles from './CustomNavbar.module.css';
import { CustomButton, CustomImage, CustomLink, CustomLoader } from '../index';
import { RootState } from "../../store/store";
import { useSelector } from "react-redux";
import logo from '../../assets/logos/DERi_hor_logo.png';
import { ReactComponent as Burger} from '../../assets/icons/icon_burger.svg';
import { ReactComponent as Account} from '../../assets/icons/icon_account.svg';
import { ReactComponent as ArrowDown} from '../../assets/icons/arrow_down.svg';
import { getForWhomMenuContent } from "../../services/contentService";

const CustomNavbar = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const isAuth = useSelector((state: RootState) => state.auth.isAuth);

    const [opacity, setOpacity] = React.useState(0);
    const [toggleDropdownMenu, setToggleDropdownMenu] = React.useState<boolean>(false);
    const [toggleMobileMenu, setToggleMobileMenu] = React.useState<boolean>(false);

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [forWhomMenuContent, setForWhomMenuContent] = useState<any>();
    
    useEffect(() => {
        document.body.style.overflowY = toggleMobileMenu ? 'hidden' : 'auto';
    }, [toggleMobileMenu]);

    useEffect(() => {
        handleGetForWhomMenuContent();

        const handleScroll = () => {
            const scrollTop = window.scrollY;
            const maxScroll = 100;
            const newOpacity = Math.min(scrollTop / maxScroll, 1);
            setOpacity(newOpacity);
        };

        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    const getBackgroudStyle = () => {

        if (toggleMobileMenu)
            return { backgroundColor: '#FFFFFF' };

        if (location.pathname.includes('account'))
            return {
                backgroundColor: `rgba(255, 255, 255,1)`,
                boxShadow: "0 2px 10px rgba(0,0,0,0.1)"
            };
        else 
            return {
                backgroundColor: `rgba(255, 255, 255, ${opacity})`,
                transition: "background-color 0.1s ease",
                boxShadow: opacity > 0 ? "0 2px 10px rgba(0,0,0,0.1)" : "none"
            };
    }

    const handleGetForWhomMenuContent = async () => {
        try {
            setIsLoading(true);

            const content = await getForWhomMenuContent();
            const menuContent = content.sections.map((item: any) => {
                return {
                    iconUrl : item.Section_presentation_card.icon?.url,
                    title: item.Section_presentation_card.title
                };
            });

            setForWhomMenuContent(menuContent);
            setIsLoading(false);
        } catch (e) {
            console.error(e);
            throw e;
        }
    }

    return (
        <>
            <nav className={ styles.containerNavbar } style={getBackgroudStyle()}>
                <div>
                    <CustomLink title={t('navbar.home')} to="/">
                        <CustomImage src={logo} alt={t('general.deri_logo')} height='110px' />
                    </CustomLink>
                </div>

                <ul className={ styles.listLinks }>
                    <li>
                        <CustomLink to="/training">{t('navbar.training')}</CustomLink>
                    </li>

                    <li>
                        <CustomLink to="/solution">{t('navbar.solution')}</CustomLink>
                    </li>

                    <li
                        className={styles.dropdown_btn}
                        onMouseEnter={() => setToggleDropdownMenu(true)}
                        onMouseLeave={() => {
                            setTimeout(() => {
                                if (!document.getElementById("dropdownMenu")?.matches(':hover')) {
                                    setToggleDropdownMenu(false);
                                }
                            }, 200)}}
                        >
                        <CustomLink to="/for_whom">{t('navbar.for_whom')}</CustomLink>

                        { toggleDropdownMenu &&
                            <ul className={styles.dropdown_menu} id="dropdownMenu">
                                
                                { !isLoading &&
                                    <>
                                        { forWhomMenuContent.map((item: any, index: number) => {
                                            return (
                                                <li key={index}>
                                                    <CustomLink className={styles.dropdown_links} to={`/for_whom/${index}`}>
                                                        <div className={styles.dropdown_icons}>
                                                            <CustomImage src={`${process.env.REACT_APP_API_BASE_URL}${item.iconUrl}`} alt="" />
                                                        </div>
                                                        <span>{item.title}</span>
                                                    </CustomLink>
                                                </li>
                                            );
                                        })}
                                    </>
                                }

                                <CustomLoader className={styles.dropdown_loader} loaderType="circular" isLoading={isLoading} />
                            </ul>
                        }
                    </li>

                    <li>
                        <CustomLink to="/store">{t('navbar.store')}</CustomLink>
                    </li>
                </ul>

                { !isAuth &&
                    <div className={styles.containerBtn}>
                        <CustomButton
                            className={styles.button}
                            onClick={() => navigate('login')} 
                            buttonType='contained'
                            endIcon={<Account />}>
                            { t('login.login') }
                        </CustomButton>
                    </div>
                }

                { isAuth &&
                    <div>
                        <CustomButton
                            onClick={() => navigate('account')}
                            endIcon={<Account />}>
                            { t('navbar.account') }
                        </CustomButton>
                    </div>
                }
            </nav>

            <nav className={ styles.containerMobileNavbar } style={getBackgroudStyle()}>
                <div onClick={() => {setToggleMobileMenu(false)}} role="button" tabIndex={0}>
                    <CustomLink title={t('navbar.home')} to="/">
                        <CustomImage src={logo} alt={t('general.deri_logo')} height='98px' />
                    </CustomLink>
                </div>

                <button
                    aria-label={t('navbar.open_menu')}
                    title={t('navbar.menu')}
                    className={styles.menu_btn}
                    onClick={() => setToggleMobileMenu(!toggleMobileMenu)}
                >
                    <Burger />
                </button>
            </nav>

            { toggleMobileMenu &&
                <div className={styles.mobile_menu}>
                    <ul>
                        <li onClick={() => {setToggleMobileMenu(false)}} role='button' tabIndex={0}>
                            <CustomLink className={styles.links} to="/training">{t('navbar.training')}</CustomLink>
                        </li>

                        <li onClick={() => {setToggleMobileMenu(false)}} role='button' tabIndex={0}>
                            <CustomLink className={styles.links} to="/solution">{t('navbar.solution')}</CustomLink>
                        </li>

                        <li>
                            <div onClick={() => {setToggleMobileMenu(false)}} className={styles.container_for_whom_link} role="button" tabIndex={0}>
                                <CustomLink className={styles.links} to="/for_whom">{t('navbar.for_whom')}</CustomLink>
                                <div>
                                    <ArrowDown width={10} />
                                </div>
                            </div>

                            <ul>
                                
                                { !isLoading &&
                                    <>
                                        { forWhomMenuContent.map((item: any, index: number) => {
                                            return (
                                                <li className={styles.sublinks} key={index} onClick={() => {setToggleMobileMenu(false)}}>
                                                    <CustomLink className={styles.sublinks_container} to={`/for_whom/${index}`}>
                                                        <div className={styles.mobile_menu_icon}>
                                                            <CustomImage src={`${process.env.REACT_APP_API_BASE_URL}${item.iconUrl}`} alt="" />
                                                        </div>
                                                        <span>{item.title}</span>
                                                    </CustomLink>
                                                </li>
                                            );
                                        })}
                                    </>
                                }

                                <CustomLoader className={styles.mobile_menu_loader} loaderType="circular" isLoading={isLoading} />
                            </ul>
                        </li>

                        <li onClick={() => {setToggleMobileMenu(false)}} role='button' tabIndex={0}>
                            <CustomLink className={styles.links} to="/store">{t('navbar.store')}</CustomLink>
                        </li>
                    </ul>

                    { !isAuth &&
                        <div className={styles.containerBtn}>
                            <CustomButton
                                className={styles.button}
                                onClick={() => { setToggleMobileMenu(false); navigate('login'); }} 
                                buttonType='contained'
                                endIcon={<Account />}>
                                { t('login.login') }
                            </CustomButton>
                        </div>
                    }

                    { isAuth &&
                        <div>
                            <CustomButton
                                onClick={() => { setToggleMobileMenu(false); navigate('account'); }}
                                endIcon={<Account />}>
                                { t('navbar.account') }
                            </CustomButton>
                        </div>
                    }
                </div>
            }
        </>
    );
}

export default CustomNavbar;